import { zodResolver } from '@hookform/resolvers/zod'
import { ClientApplicationStatus, ClientPlan, ClientType, type ClientWithMembers } from '@maru44/huntre-utils/src/models/client'
import type { ClientInvitation } from '@maru44/huntre-utils/src/models/clientInvitation'
import { type ClientMember, ClientMemberRole, ClientMemberStatus } from '@maru44/huntre-utils/src/models/clientMember'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type FormEvent, type MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { IoCopySharp, IoEllipsisVerticalSharp } from 'react-icons/io5'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { BaseDialog } from 'src/components/atoms/BaseDialog'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { ProSection } from 'src/components/organisms/pro/ProSection'
import { AuthContext } from 'src/components/providers/AuthProvider'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { baseURL, lineId } from 'src/config'
import { useClientInvitations } from 'src/hooks/client/useClientInvitations'
import { useClientMember } from 'src/hooks/client/useClientMember'
import { useDeleteClientInvitation } from 'src/hooks/client/useDeleteClientInvitation'
import { useGetClientWithMembers } from 'src/hooks/client/useGetClientWithMembers'
import { useUpdateClient } from 'src/hooks/client/useUpdateClient'
import { useStorage } from 'src/hooks/storage/useStorage'
import { useUser } from 'src/hooks/user/useUser'
import { NotAccessibleClientError } from 'src/utils/error'
import { capture } from 'src/utils/sentry'
import type { KeyedMutator } from 'swr'
import { z } from 'zod'
import { ErrorPage } from '../ErrorPage'

export const ClientPage = () => {
  const { id } = useParams()
  const { currentMember } = useContext(ClientContext)
  const { getClientWithMembers } = useGetClientWithMembers(id ?? '')

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [selectedMember, setSelectedMember] = useState<ClientMember | undefined>()
  const [action, setAction] = useState<'update:member' | 'delete:member' | 'delete:invitation' | undefined>()
  const { deleteClientMember, updateClientMemberByAdmin } = useClientMember(id ?? '', selectedMember?.id ?? '')

  const [selectedInvitation, setSelectedInvitation] = useState<ClientInvitation | undefined>()
  const { deleteInvitation } = useDeleteClientInvitation(id ?? '', selectedInvitation?.id ?? '')
  const { listClientInvitations } = useClientInvitations(id ?? '')

  const [memberRole, setMemberRole] = useState<ClientMemberRole | undefined>()

  const { clearClientLineTarget } = useUpdateClient(id ?? '')
  const { getFileURL } = useStorage()
  const [lineQR, setLineQR] = useState<string | null>(null)
  const [openClearLineDialog, setOpenClearLineDialog] = useState(false)

  useEffect(() => {
    getFileURL('static/line/M_gainfriends_2dbarcodes_GW.png', setLineQR)
  }, [getFileURL])

  const data = getClientWithMembers.data

  const handleCloseDialog = useCallback(() => {
    setSelectedMember(undefined)
    setAnchorEl(null)
    setAction(undefined)
  }, [setSelectedMember, setAnchorEl, setAction])

  const handleDeleteMember = useCallback(async () => {
    try {
      if (!selectedMember) {
        return
      }
      await deleteClientMember(selectedMember.id)
      await getClientWithMembers.mutate()
    } catch (e) {
      toast.error('メンバーが削除できませんでした')
      capture(e)
      return
    }

    toast.success('メンバーを削除しました')
    handleCloseDialog()
  }, [handleCloseDialog, deleteClientMember, getClientWithMembers.mutate, selectedMember])

  const handleUpdateMember = useCallback(async () => {
    try {
      if (!selectedMember || !memberRole) {
        return
      }
      await updateClientMemberByAdmin({
        role: memberRole,
        notificationMethods: selectedMember.notificationMethods, // とりあえずまだそのまま
      })
    } catch (e) {
      toast.error('メンバーが編集できませんでした')
      capture(e)
      return
    }
    toast.success('メンバーを編集しました')
    getClientWithMembers.mutate()
    handleCloseDialog()
  }, [memberRole, selectedMember, updateClientMemberByAdmin, handleCloseDialog, getClientWithMembers.mutate])

  const handleClearLineTarget = useCallback(async () => {
    try {
      await clearClientLineTarget()
      await getClientWithMembers.mutate()
      toast.success('LINE連携を解除しました')
      setOpenClearLineDialog(false)
    } catch (e) {
      capture(e)
      toast.error('LINE連携の解除に失敗しました')
    }
  }, [clearClientLineTarget, getClientWithMembers])

  const handleCopyLineCode = useCallback(async () => {
    if (!id) return
    await navigator.clipboard.writeText(`code: ${id}`)
    toast.success('コードをコピーしました。そのままLINEにご入力ください。')
  }, [id])

  const handleDeleteInvitation = useCallback(async () => {
    try {
      if (!selectedInvitation) {
        return
      }
      await deleteInvitation()
    } catch (e) {
      toast.error('招待を削除できませんでした')
      capture(e)
      return
    }

    toast.success('招待を削除しました')
    await listClientInvitations.mutate()
    handleCloseDialog()
  }, [handleCloseDialog, deleteInvitation, listClientInvitations.mutate, selectedInvitation])

  const isAdmin = currentMember?.role === ClientMemberRole.Admin
  const isGroup = data?.type === ClientType.Group

  const joinedMembers = useMemo(() => {
    if (!data) return []
    return data.clientMembers.filter((v) => v.status === ClientMemberStatus.Joined)
  }, [data])

  const invitations = listClientInvitations.data ?? []
  const showContent = data && ['approved', 'applied', 'reapplied'].includes(data.applicationStatus) && data.status !== 'inactive' && currentMember

  // TODO clientがapprovedじゃないときのを作る
  return (
    <Box>
      <Breads breadIds={['clients', 'client']} />
      <Stack spacing={3}>
        {showContent && (
          <>
            {/* Hero */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  alignItems: { xs: 'flex-start', sm: 'center' },
                }}
              >
                <Avatar src={data.iconURL || `${baseURL}/user.png`} sx={{ width: 72, height: 72 }} />
                <Box
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {data.name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{
                      mt: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip size="small" label={isGroup ? 'グループ' : 'ユーザー'} />
                    {isGroup && (
                      <Chip
                        size="small"
                        label={data.plan === ClientPlan.Pro ? 'Pro' : 'Free'}
                        color={data.plan === ClientPlan.Pro ? 'primary' : 'default'}
                        variant={data.plan === ClientPlan.Pro ? 'filled' : 'outlined'}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {/* Pro プラン (グループのみ表示される) */}
            <ProSection clientId={id ?? ''} />

            {/* 基本情報 */}
            {data.address && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    基本情報
                  </Typography>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      住所
                    </Typography>
                    <Typography>{data.address}</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* メンバー (グループのみ) */}
            {isGroup && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      メンバー
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {joinedMembers.length}人
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {joinedMembers.map((v) => (
                      <Member
                        member={v}
                        canEdit={v.id !== data.ownerMember.id && isAdmin}
                        isOwner={v.id === data.ownerMember.id}
                        key={v.id}
                        onClick={(e) => {
                          setSelectedMember(v)
                          setAnchorEl(e.currentTarget)
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}

            {/* LINE通知先 (グループのみ) */}
            {isGroup && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    LINE通知先
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    このグループ全体の通知を受け取るLINEトークルームです。サブグループとは独立して通知が送られます。
                    <br />
                    Proプランでは
                    <Link component={RouterLink} to={`/clients/${id ?? ''}/subGroups`}>
                      サブグループ
                    </Link>
                    ごとに別のLINE通知先を設定することもできます。
                  </Typography>
                  {data.lineTarget ? (
                    <Stack
                      direction="row"
                      spacing={2}
                      useFlexGap
                      sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Paper sx={{ width: 240, padding: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar src={data.lineTarget.pictureUrl ?? `${baseURL}/user.png`} sx={{ width: 30, height: 30 }} />
                          <Typography
                            sx={{
                              mt: 1,
                              width: '100%',
                              textAlign: 'center',
                              wordBreak: 'break-word',
                            }}
                          >
                            {data.lineTarget.displayName ?? '(名前なし)'}
                            {data.lineTarget.type === 'group' && ' (LINEグループ)'}
                            {data.lineTarget.type === 'room' && ' (LINEルーム)'}
                          </Typography>
                        </Box>
                      </Paper>
                      {isAdmin && (
                        <Button variant="outlined" onClick={() => setOpenClearLineDialog(true)}>
                          連携を解除する
                        </Button>
                      )}
                    </Stack>
                  ) : (
                    <Typography
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      未設定
                    </Typography>
                  )}
                  {isAdmin && !data.lineTarget && (
                    <Stack
                      spacing={2}
                      sx={{
                        mt: 1,
                      }}
                    >
                      <Typography>
                        以下のQRコードもしくは
                        <a href={`https://line.me/R/ti/p/@${lineId}`} target="_blank" rel="noopener">
                          こちらのリンク
                        </a>
                        から公式アカウントを友達追加・LINEグループ・LINEルームに招待してください。
                        <br />
                        その後、下記のコードを通知先のトークルームに送信するとこのグループの通知先として設定されます。
                      </Typography>
                      {lineQR && <img src={lineQR} width={240} alt="LINE QR" />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <TextField sx={{ flexGrow: 1 }} disabled value={`code: ${id ?? ''}`} />
                        <IconButton onClick={handleCopyLineCode}>
                          <IoCopySharp />
                        </IconButton>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}

            {/* 招待中 (グループのみ、招待があれば) */}
            {isGroup && invitations.length > 0 && (
              <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      招待中
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {invitations.length}件
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {invitations.map((v) => (
                      <InvitationRow
                        onClick={(e) => {
                          setSelectedInvitation(v)
                          setAnchorEl(e.currentTarget)
                        }}
                        canEdit={isAdmin}
                        member={v}
                        key={v.id}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}
          </>
        )}
        {getClientWithMembers.error && <ErrorPage exception={getClientWithMembers.error} />}
        {/* 所属していない */}
        {data && data.applicationStatus === ClientApplicationStatus.Approved && !currentMember && (
          <ErrorPage exception={new NotAccessibleClientError('not member', id ?? '')} />
        )}
      </Stack>
      {/* 招待フォーム */}
      {data && data.applicationStatus === ClientApplicationStatus.Approved && isGroup && currentMember?.status === ClientMemberStatus.Joined && isAdmin && (
        <InvitationForm data={data} id={id ?? ''} mutate={listClientInvitations.mutate} />
      )}
      {data && data.applicationStatus === ClientApplicationStatus.Approved && currentMember && (
        <>
          <Popover
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={!!anchorEl && (!!selectedMember || !!selectedInvitation)}
            onClose={() => {
              setAnchorEl(null)
              setSelectedMember(undefined)
              setSelectedInvitation(undefined)
            }}
            anchorEl={anchorEl}
          >
            <Stack>
              {selectedMember && (
                <>
                  <Divider />
                  <Typography component={Button} onClick={() => setAction('update:member')}>
                    編集する
                  </Typography>
                  <Typography component={Button} onClick={() => setAction('delete:member')}>
                    削除する
                  </Typography>
                </>
              )}
              {selectedInvitation && (
                <Typography component={Button} onClick={() => setAction('delete:invitation')}>
                  削除する
                </Typography>
              )}
            </Stack>
          </Popover>
          {/* member削除用 */}
          <BaseDialog
            open={!!selectedMember && action === 'delete:member'}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            title={`${selectedMember?.name}の削除`}
            content={`${selectedMember?.name}をグループから削除しますか？`}
            actionText="削除"
            onAction={handleDeleteMember}
          />
          {/* member編集用 */}
          <Dialog
            open={!!selectedMember && action === 'update:member'}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            title={`${selectedMember?.name}の編集`}
          >
            <DialogTitle>{selectedMember?.name}の編集</DialogTitle>
            <DialogContent>
              <InputLabel>権限</InputLabel>
              <Select
                fullWidth
                required
                id="memberRole"
                name="memberRole"
                onChange={(e) => {
                  setMemberRole(e.target.value as ClientMemberRole)
                }}
                defaultValue={selectedMember?.role ?? 'member'}
              >
                <MenuItem value="member">通常メンバー</MenuItem>
                <MenuItem value="admin">管理メンバー</MenuItem>
              </Select>
            </DialogContent>
            <DialogActions>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  p: 1,
                  justifyContent: 'space-between',
                }}
              >
                <Button variant="outlined" onClick={handleCloseDialog}>
                  キャンセル
                </Button>
                <Button variant="contained" onClick={handleUpdateMember}>
                  編集
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
          {/* invitation削除用 */}
          <BaseDialog
            open={!!selectedInvitation && action === 'delete:invitation'}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            title={`${selectedInvitation?.email}への招待の削除`}
            content={`${selectedInvitation?.email}の招待を削除しますか？`}
            actionText="削除"
            onAction={handleDeleteInvitation}
          />
          {/* LINE連携解除 */}
          <BaseDialog
            open={openClearLineDialog}
            title="LINE連携の解除"
            content="LINE連携を解除しますか？"
            actionText="解除する"
            onClose={() => setOpenClearLineDialog(false)}
            onAction={handleClearLineTarget}
          />
        </>
      )}
    </Box>
  )
}

// TODO メンバー操作 (adminなら)
// 通知方法は自分で
// 追加 削除
// role等の編集

type ClientMemberLike = {
  email: string
  role: ClientMemberRole
  name?: string
  iconURL?: string
  status?: ClientMemberStatus
}

type MemberProps = {
  member: ClientMemberLike
  canEdit: boolean
  isOwner?: boolean
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
}

const roleChip = (member: ClientMemberLike, isOwner: boolean) => {
  if (isOwner) return <Chip size="small" label="オーナー" color="primary" variant="outlined" />
  if (member.role === ClientMemberRole.Admin) return <Chip size="small" label="管理" color="info" variant="outlined" />
  return <Chip size="small" label="メンバー" variant="outlined" />
}

const Member = ({ member, canEdit, isOwner, onClick }: MemberProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        py: 0.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Avatar src={member.iconURL ?? `${baseURL}/user.png`} sx={{ width: 36, height: 36 }} />
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography noWrap variant="body1">
            {member.name ?? member.email}
          </Typography>
          {member.name && (
            <Typography
              noWrap
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {member.email}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        {roleChip(member, !!isOwner)}
        {member.status === ClientMemberStatus.Retired && <Chip size="small" label="脱退" color="error" />}
        {member.status === ClientMemberStatus.Joined && canEdit && onClick && (
          <IconButton size="small" onClick={onClick}>
            <IoEllipsisVerticalSharp />
          </IconButton>
        )}
      </Stack>
    </Box>
  )
}

const InvitationRow = ({ member, canEdit, onClick }: MemberProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        py: 0.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Avatar src={member.iconURL ?? `${baseURL}/user.png`} sx={{ width: 36, height: 36 }} />
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography noWrap variant="body1">
            {member.name ?? member.email}
          </Typography>
          {member.name && (
            <Typography
              noWrap
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {member.email}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Chip size="small" label={member.role === ClientMemberRole.Admin ? '管理として招待中' : 'メンバーとして招待中'} variant="outlined" />
        {canEdit && onClick && (
          <IconButton size="small" onClick={onClick}>
            <IoEllipsisVerticalSharp />
          </IconButton>
        )}
      </Stack>
    </Box>
  )
}

const schema = z.object({
  email: z.string().email(),
  role: z.string(),
})
export type FormState = z.infer<typeof schema>
const defaultValues: FormState = {
  email: '',
  role: 'member',
}

type InvitationFormProps = {
  id: string
  data: ClientWithMembers
  mutate: KeyedMutator<ClientInvitation[]>
}

const InvitationForm = ({ id, data, mutate }: InvitationFormProps) => {
  const { control, reset, handleSubmit, formState } = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'all',
  })

  const [user] = useContext(AuthContext)

  const { getUser } = useUser(user?.uid ?? '')
  const { createInvitation } = useClientInvitations(id)

  const submit = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault()
        const invitedBy = getUser.data?.data
        if (!invitedBy || !data) {
          return
        }
        await handleSubmit((v) => createInvitation(v.email, v.role as ClientMemberRole, data, invitedBy))()
      } catch (e) {
        capture(e)
        toast.error('招待に失敗しました')
        return
      }
      reset()
      toast.success('招待に成功しました')
      await mutate()
    },
    [handleSubmit, createInvitation, reset, getUser.data?.data, data, mutate],
  )

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }} component="form" onSubmit={submit}>
      <Stack spacing={2}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
          }}
        >
          メンバーを招待
        </Typography>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Box>
              <InputLabel>メールアドレス</InputLabel>
              <TextField required fullWidth type="email" {...field} error={!!formState.errors?.email} helperText={formState.errors?.email?.message} />
            </Box>
          )}
        />
        <Controller
          name="role"
          control={control}
          defaultValue={ClientMemberRole.Member}
          render={({ field }) => (
            <Box>
              <InputLabel>役割</InputLabel>
              <Select
                fullWidth
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value as string)
                }}
              >
                <MenuItem value={ClientMemberRole.Member}>通常メンバー</MenuItem>
                <MenuItem value={ClientMemberRole.Admin}>管理メンバー</MenuItem>
              </Select>
            </Box>
          )}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="contained" type="submit">
            招待する
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
